import { getEnvironment } from "./environment";
import { FEATURES, FEATURE_OVERRIDE_PARAM_PREFIX, TRUTHY_VALUES, FALSY_VALUES } from "../constants/features";
import type { Feature, FeatureOverrides, FeatureSearchParams } from "../types";

export const FEATURE_SEARCH_PARAM_KEYS = FEATURES.map(
  (feature) => `${FEATURE_OVERRIDE_PARAM_PREFIX}${feature}` as keyof FeatureSearchParams
);

export const isValidFeature = (value: unknown): value is Feature => FEATURES.includes(value as Feature);

export const parseFeatureSearchParams = (search: Record<string, unknown>): FeatureSearchParams =>
  Object.entries(search).reduce<FeatureSearchParams>((acc, [k, v]) => {
    if (
      k.startsWith(FEATURE_OVERRIDE_PARAM_PREFIX) &&
      isValidFeature(k.replace(FEATURE_OVERRIDE_PARAM_PREFIX, "")) &&
      typeof v === "string"
    ) {
      acc[k as keyof FeatureSearchParams] = v;
    }
    return acc;
  }, {});

export const getFeatureSearchParams = (): Record<string, string> => {
  const normalized: Record<string, string> = {};

  new URLSearchParams(window.location.search).forEach((value, name) => {
    if (!name.startsWith(FEATURE_OVERRIDE_PARAM_PREFIX)) return;

    const lower = value.toLowerCase();
    if (TRUTHY_VALUES.includes(lower)) normalized[name] = "1";
    else if (FALSY_VALUES.includes(lower)) normalized[name] = "0";
  });

  return parseFeatureSearchParams(normalized) as Record<string, string>;
};

export const getFeatureOverridesFromUrl = (): FeatureOverrides =>
  Object.entries(getFeatureSearchParams()).reduce<FeatureOverrides>((acc, [key, value]) => {
    const feature = key.replace(FEATURE_OVERRIDE_PARAM_PREFIX, "");
    if (isValidFeature(feature)) {
      acc[feature] = value === "1";
    }
    return acc;
  }, {});

export const buildUrlWithFeatureOverrides = (
  overrides: FeatureOverrides,
  currentUrl: string | URL = window.location.href
): URL => {
  const url = new URL(currentUrl.toString());

  FEATURE_SEARCH_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));
  Object.entries(overrides)
    .sort(([featureA], [featureB]) => featureA.localeCompare(featureB))
    .forEach(([feature, enabled]) => {
      if (isValidFeature(feature) && typeof enabled === "boolean") {
        url.searchParams.set(`${FEATURE_OVERRIDE_PARAM_PREFIX}${feature}`, enabled ? "1" : "0");
      }
    });

  return url;
};

export const isFeatureRunning = (name: Feature): boolean => {
  if (getEnvironment().internal) {
    const overrides = getFeatureSearchParams();
    const key = FEATURE_OVERRIDE_PARAM_PREFIX + name;
    if (key in overrides) return overrides[key] === "1";
  }

  return Boolean(getEnvironment().features?.[name]);
};
